import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Clock, ListChecks, Plane, Thermometer, CalendarDays, FileText, User,
  LayoutDashboard, CheckCircle2, Users, CalendarOff, Palette, Settings,
  Building2, LogOut, Menu, X, Briefcase, ClipboardCheck, BarChart3, ShieldAlert, Mail, Contact, TrendingUp,
} from "lucide-react";

const NAV = {
  employee: [
    { to: "/app/stempeluhr", label: "Stempeluhr", icon: Clock },
    { to: "/app/zeiten", label: "Meine Zeiten", icon: ListChecks },
    { to: "/app/urlaub", label: "Abwesenheit", icon: Plane },
    { to: "/app/schichten", label: "Schichten", icon: CalendarDays },
    { to: "/app/verfuegbarkeit", label: "Verfügbarkeit", icon: CalendarOff },
    { to: "/app/auftraege", label: "Aufträge", icon: Briefcase, orders: true },
    { to: "/app/projektstunden", label: "Projektstunden", icon: TrendingUp, margin: true },
    { to: "/app/lohn", label: "Lohnabrechnungen", icon: FileText },
    { to: "/app/profil", label: "Profil", icon: User },
  ],
  admin: [
    { to: "/app/dashboard", label: "Live-Dashboard", icon: LayoutDashboard },
    { to: "/app/freigaben", label: "Freigaben", icon: CheckCircle2 },
    { to: "/app/mitarbeiter", label: "Mitarbeiter", icon: Users },
    { to: "/app/abteilungen", label: "Abteilungen", icon: Building2 },
    { to: "/app/schichtplan", label: "Schichtplanung", icon: CalendarDays },
    { to: "/app/nachweise", label: "Nachweis-Archiv", icon: ClipboardCheck, orders: true },
    { to: "/app/abwesenheiten", label: "Abwesenheiten", icon: CalendarOff },
    { to: "/app/reports", label: "Reports", icon: BarChart3 },
    { to: "/app/kunden", label: "Kunden", icon: Contact, margin: true },
    { to: "/app/margin", label: "Margin Report", icon: TrendingUp, margin: true },
    { to: "/app/branding", label: "Branding", icon: Palette },
    { to: "/app/einstellungen", label: "Einstellungen", icon: Settings },
    { section: "Persönlich" },
    { to: "/app/stempeluhr", label: "Stempeluhr", icon: Clock },
    { to: "/app/zeiten", label: "Meine Zeiten", icon: ListChecks },
    { to: "/app/urlaub", label: "Meine Abwesenheit", icon: Plane },
    { to: "/app/schichten", label: "Meine Schichten", icon: CalendarDays },
    { to: "/app/verfuegbarkeit", label: "Meine Verfügbarkeit", icon: CalendarOff },
    { to: "/app/auftraege", label: "Aufträge", icon: Briefcase, orders: true },
    { to: "/app/lohn", label: "Lohnabrechnungen", icon: FileText },
    { to: "/app/profil", label: "Profil", icon: User },
  ],
  super_admin: [
    { to: "/app/firmen", label: "Firmen", icon: Building2 },
    { to: "/app/kontaktanfragen", label: "Kontaktanfragen", icon: Mail },
    { to: "/app/profil", label: "Profil", icon: User },
  ],
};

const ROLE_LABEL = { employee: "Mitarbeiter", admin: "Administrator", super_admin: "Super-Admin" };

export default function Layout() {
  const { user, company, branding, logout, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const hasOrders = (company?.features || []).includes("orders");
  const hasMargin = (company?.features || []).includes("margin");
  const items = (NAV[user?.role] || []).filter((it) => (!it.orders || hasOrders) && (!it.margin || hasMargin));
  const companyName = branding?.company_name || company?.name || "Teamtempus";
  const logo = branding?.logo_base64;
  const impersonating = !!user?.impersonating;

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const doExitImpersonation = async () => {
    await exitImpersonation();
    navigate("/app/firmen", { replace: true });
  };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-2 py-4">
        {logo ? (
          <img src={logo} alt="Logo" className="h-9 w-9 rounded-lg object-cover" />
        ) : (
          <div className="h-9 w-9 rounded-lg brand-bg flex items-center justify-center text-white font-bold">
            {companyName.charAt(0)}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-bold text-slate-900 truncate text-sm">{companyName}</div>
          <div className="text-xs text-slate-400">Teamtempus</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 mt-2">
        {items.map((it, idx) =>
          it.section ? (
            <div key={`sec-${idx}`} className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{it.section}</div>
          ) : (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${it.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "text-white brand-bg" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </NavLink>
          )
        )}
      </nav>
      <div className="border-t border-slate-200 pt-3 mt-3">
        <div className="px-3 pb-2">
          <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-400">{ROLE_LABEL[user?.role]}</div>
        </div>
        <Button
          variant="ghost"
          onClick={doLogout}
          data-testid="logout-button"
          className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50"
        >
          <LogOut className="h-[18px] w-[18px] mr-3" /> Abmelden
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col justify-between p-4">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-slate-200 p-4">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {impersonating && (
          <div className="bg-amber-500 text-white px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-sm" data-testid="impersonation-banner">
            <span className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <span className="truncate">Betreiber-Modus: Sie verwalten <strong>{user?.impersonated_company_name || companyName}</strong></span>
            </span>
            <Button size="sm" variant="secondary" onClick={doExitImpersonation} data-testid="exit-impersonation" className="bg-white text-amber-700 hover:bg-amber-50 shrink-0">
              Beenden
            </Button>
          </div>
        )}
        <header className="h-16 sticky top-0 z-40 brand-header-bg border-b border-black/10 flex items-center justify-between px-4 sm:px-6 shadow-sm" data-testid="portal-header">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-white"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X /> : <Menu />}
            </button>
            {logo ? (
              <img src={logo} alt="Logo" className="h-9 w-9 rounded-lg object-cover bg-white/95 p-0.5 ring-1 ring-white/40" data-testid="portal-header-logo" />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-white/20 flex items-center justify-center text-white font-bold">
                {companyName.charAt(0)}
              </div>
            )}
            <span className="font-bold text-white text-lg tracking-tight truncate max-w-[50vw]" data-testid="portal-header-company">{companyName}</span>
          </div>
          <div className="text-sm text-white/80 hidden sm:block">{ROLE_LABEL[user?.role]}</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
